import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  BarChartRounded,
  NotesRounded,
  OpenInNewRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AdaptivePopover from "../../../common/AdaptivePopover";
import MenuRow from "../../../common/MenuRow";
import { hapticSelection } from "../../../../lib/haptics";
import type { TonalTokens } from "../../../polish/tokens";
import { REPORT_ROWS_GAP, ResultsCard } from "./ResultsCard";
import { ResultsCardSkeleton } from "./FeedSkeletons";
import { takeFeedAnchor } from "./feedAnchor";
import { groupByReportDay, itemKey } from "./feedModel";
import { dayHeadingSx } from "./headings";
import { useMinute } from "../hooks/useMinute";
import { earningsCompanyPath } from "../earningsNavigation";
import type { EarningsFeedItem } from "./types";

/** Prefetch distance: ≈ 1.5 phone viewports, four or five report cards ahead (§7.2). */
export const PREFETCH_MARGIN = "1200px 0px";

/** Past this many pages auto-load stops and `Show more` takes over (§7.4). */
export const SOFT_PAGE_CAP = 60;

const valuationPath = (symbol: string) =>
  `/charts?ticker=${encodeURIComponent(symbol)}&tab=valuation`;
const transcriptPath = (symbol: string) =>
  `${earningsCompanyPath(symbol)}?view=transcripts`;

const SAFE_KEY = /^[\w:.-]+$/;

interface EarningsFeedProps {
  items: EarningsFeedItem[];
  tokens: TonalTokens;
  isDark: boolean;
  reduced: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** The last next-page fetch failed (earlier pages stay on screen). */
  nextPageFailed: boolean;
  /** Pages loaded so far, for the soft cap. */
  pageCount: number;
  fetchNextPage: () => void;
  onOpenReport: (eventId: string, key: string, card: HTMLElement) => void;
  onOpenCompany: (symbol: string) => void;
  /** The hub's history entry: Back restores the card that was opened from it. */
  anchorEntry: string;
}

/**
 * The hub's report cards under their report days, newest report first,
 * twenty a page. The next page loads well before the end, with a card's
 * shape while it does; after a failed page, `Try again`; past the soft cap or
 * without an IntersectionObserver, `Show more`. The end of the feed shows
 * nothing. The ⋯ menu is a sheet on touch (AdaptivePopover).
 */
export const EarningsFeed = ({
  items,
  tokens,
  isDark,
  reduced,
  hasNextPage,
  isFetchingNextPage,
  nextPageFailed,
  pageCount,
  fetchNextPage,
  onOpenReport,
  onOpenCompany,
  anchorEntry,
}: EarningsFeedProps) => {
  const navigate = useNavigate();
  const now = useMinute();
  const days = useMemo(() => groupByReportDay(items, now), [items, now]);
  const feed = useRef<HTMLDivElement | null>(null);

  const [menu, setMenu] = useState<{
    anchor: HTMLElement;
    symbol: string;
  } | null>(null);

  const onOpenMenu = useCallback((anchor: HTMLElement, symbol: string) => {
    hapticSelection();
    setMenu({ anchor, symbol });
  }, []);

  // Each card knows only its event; the feed adds the item's key so Back
  // can find the card again.
  const openReport = useRef(onOpenReport);
  openReport.current = onOpenReport;
  const openFromCard = useCallback((eventId: string, card: HTMLElement) => {
    openReport.current(eventId, card.dataset.feedKey ?? "", card);
  }, []);

  // Back from a report: put the card that was opened where it was.
  useLayoutEffect(() => {
    const anchor = takeFeedAnchor(anchorEntry);
    if (anchor === null || !SAFE_KEY.test(anchor.key)) return;
    const card = feed.current?.querySelector(`[data-feed-key="${anchor.key}"]`);
    if (!card) return;
    window.scrollTo(
      0,
      window.scrollY + card.getBoundingClientRect().top - anchor.top,
    );
    // Only the first render can restore; later ones are the reader's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const observable = typeof IntersectionObserver !== "undefined";
  const capped = pageCount >= SOFT_PAGE_CAP;
  const autoLoad =
    observable &&
    !capped &&
    hasNextPage &&
    !isFetchingNextPage &&
    !nextPageFailed;
  const showMore =
    hasNextPage && !isFetchingNextPage && !nextPageFailed && !autoLoad;

  const sentinel = useRef<HTMLDivElement | null>(null);
  const loadMore = useRef(fetchNextPage);
  loadMore.current = fetchNextPage;
  useEffect(() => {
    const node = sentinel.current;
    if (!autoLoad || node === null) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore.current();
      },
      { rootMargin: PREFETCH_MARGIN },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [autoLoad]);

  const go = (path: string) => {
    hapticSelection();
    setMenu(null);
    navigate(path);
  };

  const quietButtonSx = {
    minHeight: 44,
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.875rem",
    borderRadius: "999px",
    px: 2,
    color: tokens.muted,
    "@media (hover: hover)": {
      "&:hover": { color: tokens.text, backgroundColor: tokens.raised },
    },
    "&:focus-visible": {
      outline: `2px solid ${tokens.gain}`,
      outlineOffset: 2,
    },
  } as const;

  return (
    <Box data-testid="earnings-feed" ref={feed}>
      {days.map((group, index) => (
        <Box
          key={group.day}
          component="section"
          aria-label={group.label}
          sx={{ mt: index === 0 ? 0 : { xs: 4, md: 5 } }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
          >
            <Typography
              component="h3"
              data-testid="feed-day"
              sx={dayHeadingSx(tokens)}
            >
              {group.label}
            </Typography>
            <Box
              aria-hidden
              sx={{ flex: 1, height: "1px", backgroundColor: tokens.hairline }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: REPORT_ROWS_GAP,
            }}
          >
            {group.items.map((item) => (
              <ResultsCard
                key={itemKey(item)}
                item={item}
                tokens={tokens}
                isDark={isDark}
                reduced={reduced}
                onOpenReport={openFromCard}
                onOpenCompany={onOpenCompany}
                onOpenMenu={onOpenMenu}
              />
            ))}
          </Box>
        </Box>
      ))}

      {isFetchingNextPage && (
        <Box data-testid="next-page-skeleton" sx={{ mt: REPORT_ROWS_GAP }}>
          <ResultsCardSkeleton tokens={tokens} notes />
        </Box>
      )}
      {hasNextPage && !isFetchingNextPage && nextPageFailed && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button onClick={() => fetchNextPage()} sx={quietButtonSx}>
            Try again
          </Button>
        </Box>
      )}
      {showMore && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button onClick={() => fetchNextPage()} sx={quietButtonSx}>
            Show more
          </Button>
        </Box>
      )}
      {autoLoad && <div ref={sentinel} data-testid="feed-sentinel" />}

      <AdaptivePopover
        open={menu !== null}
        anchorEl={menu?.anchor ?? null}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuRow
          icon={<OpenInNewRounded fontSize="small" />}
          label="Open company earnings"
          onClick={() => menu && go(earningsCompanyPath(menu.symbol))}
        />
        <MenuRow
          icon={<BarChartRounded fontSize="small" />}
          label="Open Valuation"
          onClick={() => menu && go(valuationPath(menu.symbol))}
        />
        <MenuRow
          icon={<NotesRounded fontSize="small" />}
          label="Open transcript"
          onClick={() => menu && go(transcriptPath(menu.symbol))}
        />
      </AdaptivePopover>
    </Box>
  );
};
